import * as ko from "knockout";
import {DEFAULT_SEARCH_PAGE, INPUT_DATE_FORMAT, MAX_RESULTS_PER_PAGE} from "../../constants";
import {global} from "../../appContext";
import {
    IImportHrFile,
    IMessageData,
    IResponse,
    IRouteDefinition,
    IRouteDefinitionParams,
    ISearchParams
} from "../../index";
import PromiseRequest from "../../req/PromiseRequest";
import {storeObservable} from "../../util/sessionstore";
import {KOSubscriptionTracker} from "../../util/KOSubscriptionTracker";
import {memoryStore} from "../../util/memorystore";
import {createErrorMessage} from "../../util/misc";
import {convertFromServerString, getDateTime} from "../../util/datetime";
import {getImportHrFileStarted} from "../../util/request";
import {moment} from 'moment';
import {setRouteHash} from "../../constants/RouteDefinitions";
import {nsRoute} from "../../util/route";
import {REQUEST_MESSAGE} from "../../constants/Language";
import {IParams as IEllipsisDropdownParams} from "../button/ellipsis-dropdown";
import {getSearchBoxHeadingKey} from "../search/boxHeadingKey";
import {nsFile} from "../sync/status-v2/nsFile";
import {nsFa} from "../fa/fn";
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import i = nsFa.i;
import searchHtml from './search.html?raw';

export let template: string = searchHtml;

/** Success-count columns that can be filtered for value > 0. */
type TCountColumn =
    | 'newaccounts'
    | 'editedaccounts'
    | 'deletedaccounts'
    | 'recoveredaccounts'
    | 'newgroups'
    | 'editedgroups'
    | 'deletedgroups'
    | 'newdepartments'
    | 'editeddepartments'
    | 'deleteddepartments';

type TDatePreset = '24h' | '7d' | '30d' | 'custom';
type TSyncTypeFilter = 'all' | 'full' | 'delta';

interface IColumnFilterOption {
    key: TCountColumn;
    label: string;
}

interface IColumnFilterGroup {
    header: string;
    items: IColumnFilterOption[];
}

interface IDatePresetOption {
    key: Exclude<TDatePreset, 'custom'>;
    label: string;
}

interface ISyncTypeOption {
    key: TSyncTypeFilter;
    label: string;
}

const DEFAULT_DATE_PRESET: Exclude<TDatePreset, 'custom'> = '30d';

function defaultFromDate(): string {
    return moment().subtract(30, 'days').format(INPUT_DATE_FORMAT);
}

function defaultToDate(): string {
    return moment().format(INPUT_DATE_FORMAT);
}

export class viewModel extends ParsedRouteDataDecorator {
    private allData: KnockoutObservableArray<IImportHrFile> = ko.observableArray<IImportHrFile>([]);
    private data: KnockoutObservableArray<IImportHrFile> = ko.observableArray<IImportHrFile>([]);
    private isDeltaSyncSupported: KnockoutObservable<boolean> = ko.observable(false);
    private isReady: KnockoutObservable<boolean | null> = ko.observable(null);
    private isWaiting: KnockoutObservable<boolean> = memoryStore.get("customer.importhistory.iswaiting");
    private kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private reqSearch: PromiseRequest = global.getPromiseRequest("searchimporthrfile");
    private req_sendendreport: PromiseRequest = global.getPromiseRequest("sendendreport");
    private searchpagenr: KnockoutObservable<number> = memoryStore.get('import.history.pagenr', DEFAULT_SEARCH_PAGE);
    private readonly resultsprpage = storeObservable("num.search.results");
    private searchpages: KnockoutObservable<number | null> = ko.observable(null);
    private searchtotalresults: KnockoutObservable<number | null> = ko.observable(null);
    private readonly routeDef: IRouteDefinition | undefined;

    private suppressDateSync = false;
    private readonly maxdate: string = defaultToDate();
    private readonly datePreset: KnockoutObservable<TDatePreset> = memoryStore.get(
        'import.history.filter.datepreset',
        DEFAULT_DATE_PRESET,
    );
    private readonly filterFromDate: KnockoutObservable<string> = memoryStore.get(
        'import.history.filter.fromdate',
        defaultFromDate(),
    );
    private readonly filterToDate: KnockoutObservable<string> = memoryStore.get(
        'import.history.filter.todate',
        defaultToDate(),
    );
    private readonly filterSyncType: KnockoutObservable<TSyncTypeFilter> = memoryStore.get(
        'import.history.filter.synctype',
        'all',
    );
    private readonly filterColumns: KnockoutObservableArray<TCountColumn> = ko.observableArray(
        (memoryStore.get('import.history.filter.columns', [])() as TCountColumn[]).slice(),
    );
    private readonly isDateMenuOpen: KnockoutObservable<boolean> = ko.observable(false);
    private readonly isTypeMenuOpen: KnockoutObservable<boolean> = ko.observable(false);
    private readonly isColumnsMenuOpen: KnockoutObservable<boolean> = ko.observable(false);

    private readonly datePresetOptions: IDatePresetOption[] = [
        {key: '24h', label: 'search.filter.last24hours'},
        {key: '7d', label: 'search.filter.last7days'},
        {key: '30d', label: 'search.filter.last30days'},
    ];

    private readonly syncTypeOptions: ISyncTypeOption[] = [
        {key: 'all', label: 'all'},
        {key: 'full', label: 'full'},
        {key: 'delta', label: 'Delta'},
    ];

    private readonly columnFilterGroups: IColumnFilterGroup[] = [
        {
            header: 'import.history.filter.accountchanges',
            items: [
                {key: 'newaccounts', label: 'new|p'},
                {key: 'editedaccounts', label: 'changed'},
                {key: 'deletedaccounts', label: 'deleted'},
                {key: 'recoveredaccounts', label: 'restored'},
            ],
        },
        {
            header: 'import.history.filter.groupchanges',
            items: [
                {key: 'newgroups', label: 'new'},
                {key: 'editedgroups', label: 'changed'},
                {key: 'deletedgroups', label: 'deleted'},
            ],
        },
        {
            header: 'import.history.filter.departmentchanges',
            items: [
                {key: 'newdepartments', label: 'new'},
                {key: 'editeddepartments', label: 'changed'},
                {key: 'deleteddepartments', label: 'deleted'},
            ],
        },
    ];

    private readonly dateFilterLabel: KnockoutComputed<string> = ko.pureComputed(() => {
        const preset = this.datePreset();
        const match = this.datePresetOptions.find((o) => o.key === preset);
        if (match) {
            return match.label;
        }
        const formatDisplay = (raw: string): string => {
            const m = moment(raw, INPUT_DATE_FORMAT);
            return m.isValid() ? m.format('DD.MM.YYYY') : raw;
        };
        const from = this.filterFromDate();
        const to = this.filterToDate();
        if (from && to) {
            return `${formatDisplay(from)} – ${formatDisplay(to)}`;
        }
        if (from || to) {
            return formatDisplay(from || to);
        }
        return 'search.filter.customrange';
    });

    /** Preset / empty custom = translation key; chosen custom range = formatted date. */
    private readonly dateFilterLabelIsKey: KnockoutComputed<boolean> = ko.pureComputed(() => {
        if (this.datePreset() !== 'custom') {
            return true;
        }
        return !this.filterFromDate() && !this.filterToDate();
    });

    private readonly syncTypeLabel: KnockoutComputed<string> = ko.pureComputed(() => {
        return this.syncTypeOptions.find((o) => o.key === this.filterSyncType())?.label ?? 'all';
    });

    private readonly columnFilterCount: KnockoutComputed<number> = ko.pureComputed(
        () => this.filterColumns().length,
    );

    private readonly columnFilterLabel: KnockoutComputed<string> = ko.pureComputed(() => {
        const count = this.columnFilterCount();
        if (count > 0) {
            return `import.history.filter.columnscount|count:${count}`;
        }
        return 'import.history.filter.columns';
    });

    private readonly showEmptyFilteredState: KnockoutComputed<boolean> = ko.pureComputed(() => {
        return !!this.isReady() && this.allData().length > 0 && this.searchtotalresults() === 0;
    });

    private readonly showTable: KnockoutComputed<boolean> = ko.pureComputed(() => {
        return !!this.isReady() && (this.data().length > 0 || this.showEmptyFilteredState());
    });

    private readonly searchBoxHeading = ko.pureComputed(() => getSearchBoxHeadingKey(this.__routeDef));

    constructor(params: IRouteDefinitionParams) {
        super(params);
        this.routeDef = params.__routeDef;
        this.kosub.subscribe(global.login.selectedOrganization, () => {
            this.searchpagenr(0);
            this.init();
        });
        this.kosub.subscribe(this.resultsprpage, () => {
            this.searchpagenr(0);
            this.applyDisplayPage();
        });
        this.kosub.subscribe(this.filterFromDate, () => this.onCustomDateChanged());
        this.kosub.subscribe(this.filterToDate, () => this.onCustomDateChanged());
        this.kosub.subscribe(this.filterSyncType, () => this.onFilterChanged());
        this.kosub.subscribe(this.filterColumns, () => {
            memoryStore.get('import.history.filter.columns')(this.filterColumns().slice());
            this.onFilterChanged();
        });

        this.init();
    }

    private init() {
        this.isDeltaSyncSupported(!!global.login.selectedOrganization()?.deltasync);
        if (!this.isDeltaSyncSupported() && this.filterSyncType() !== 'all') {
            this.filterSyncType('all');
        }
        this.doRequest();
    }

    private onFilterChanged() {
        this.searchpagenr(0);
        this.applyDisplayPage();
    }

    private onCustomDateChanged() {
        if (this.suppressDateSync) {
            return;
        }
        this.datePreset('custom');
        this.onFilterChanged();
    }

    private boundsForPreset(preset: Exclude<TDatePreset, 'custom'>): {from: moment.Moment; to: moment.Moment} {
        const to = moment();
        switch (preset) {
            case '24h':
                return {from: moment().subtract(24, 'hours'), to};
            case '7d':
                return {from: moment().subtract(7, 'days').startOf('day'), to: to.clone().endOf('day')};
            case '30d':
            default:
                return {from: moment().subtract(30, 'days').startOf('day'), to: to.clone().endOf('day')};
        }
    }

    private getActiveDateBounds(): {from: moment.Moment | null; to: moment.Moment | null} {
        const preset = this.datePreset();
        if (preset !== 'custom') {
            return this.boundsForPreset(preset);
        }
        const fromRaw = this.filterFromDate();
        const toRaw = this.filterToDate();
        return {
            from: fromRaw ? moment(fromRaw, INPUT_DATE_FORMAT).startOf('day') : null,
            to: toRaw ? moment(toRaw, INPUT_DATE_FORMAT).endOf('day') : null,
        };
    }

    private selectDatePreset(preset: Exclude<TDatePreset, 'custom'>, _: unknown, event: Event) {
        event.preventDefault();
        event.stopPropagation();
        this.suppressDateSync = true;
        const bounds = this.boundsForPreset(preset);
        this.datePreset(preset);
        this.filterFromDate(bounds.from.format(INPUT_DATE_FORMAT));
        this.filterToDate(bounds.to.format(INPUT_DATE_FORMAT));
        this.suppressDateSync = false;
        this.closeAllMenus();
        this.onFilterChanged();
    }

    private clearAllFilters() {
        this.suppressDateSync = true;
        this.datePreset(DEFAULT_DATE_PRESET);
        const bounds = this.boundsForPreset(DEFAULT_DATE_PRESET);
        this.filterFromDate(bounds.from.format(INPUT_DATE_FORMAT));
        this.filterToDate(bounds.to.format(INPUT_DATE_FORMAT));
        this.suppressDateSync = false;
        this.filterSyncType('all');
        this.filterColumns([]);
        this.closeAllMenus();
        this.onFilterChanged();
    }

    private clearColumnFilters(_: unknown, event: Event) {
        event.preventDefault();
        event.stopPropagation();
        this.filterColumns([]);
    }

    private columnOptionId(key: TCountColumn): string {
        return `importhistory-colfilter-${key}`;
    }

    /** Prevent focus loss so the menu stays open while toggling checkboxes. */
    private onColumnsMenuMouseDown(_: unknown, event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();
        return true;
    }

    private onColumnsMenuClick(_: unknown, event: MouseEvent) {
        event.stopPropagation();
        return true;
    }

    private closeAllMenus() {
        this.isDateMenuOpen(false);
        this.isTypeMenuOpen(false);
        this.isColumnsMenuOpen(false);
    }

    private closeMenuIfFocusLeft(
        menuOpen: KnockoutObservable<boolean>,
        current: HTMLElement | null,
    ) {
        window.setTimeout(() => {
            if (!menuOpen()) {
                return;
            }
            const active = document.activeElement;
            if (current && active && current.contains(active)) {
                return;
            }
            menuOpen(false);
        }, 0);
    }

    private selectSyncType(type: TSyncTypeFilter, _: unknown, event: Event) {
        event.preventDefault();
        event.stopPropagation();
        this.filterSyncType(type);
        this.isTypeMenuOpen(false);
    }

    private onDateToggleClick(_: unknown, event: Event) {
        event.preventDefault();
        event.stopPropagation();
        const next = !this.isDateMenuOpen();
        this.closeAllMenus();
        this.isDateMenuOpen(next);
    }

    private onDateFocusOut(_: unknown, event: FocusEvent) {
        this.closeMenuIfFocusLeft(this.isDateMenuOpen, event.currentTarget as HTMLElement | null);
    }

    private onTypeToggleClick(_: unknown, event: Event) {
        event.preventDefault();
        event.stopPropagation();
        const next = !this.isTypeMenuOpen();
        this.closeAllMenus();
        this.isTypeMenuOpen(next);
    }

    private onTypeFocusOut(_: unknown, event: FocusEvent) {
        this.closeMenuIfFocusLeft(this.isTypeMenuOpen, event.currentTarget as HTMLElement | null);
    }

    private onColumnsToggleClick(_: unknown, event: Event) {
        event.preventDefault();
        event.stopPropagation();
        const next = !this.isColumnsMenuOpen();
        this.closeAllMenus();
        this.isColumnsMenuOpen(next);
    }

    private onColumnsFocusOut(_: unknown, event: FocusEvent) {
        this.closeMenuIfFocusLeft(this.isColumnsMenuOpen, event.currentTarget as HTMLElement | null);
    }

    private doRequest() {
        this.isReady(false);
        this.isWaiting(true);
        if (!this.allData().length) {
            this.messageData({message: REQUEST_MESSAGE});
        }
        this.reqSearch.abort();
        this.reqSearch
            .request(this.getParams(), true)
            .then((resp: IResponse) => {
                this.allData(resp.data as IImportHrFile[]);
                this.applyDisplayPage();
                this.isWaiting(false);
                this.isReady(true);
            })
            .catch((resp: IResponse | string) => {
                this.isWaiting(false);
                const reason = typeof resp == 'string' ? resp : resp.lasterror;
                this.messageData(createErrorMessage(String(reason)));
            });
    }

    private getFilteredRows(): IImportHrFile[] {
        const {from: fromMoment, to: toMoment} = this.getActiveDateBounds();
        const syncType = this.filterSyncType();
        const columns = this.filterColumns();

        return this.allData().filter((item) => {
            if (syncType === 'full' && item.sequencenr) {
                return false;
            }
            if (syncType === 'delta' && !item.sequencenr) {
                return false;
            }

            if (fromMoment?.isValid() || toMoment?.isValid()) {
                const started = convertFromServerString(item.started);
                if (!started.isValid()) {
                    return false;
                }
                if (fromMoment?.isValid() && started.isBefore(fromMoment)) {
                    return false;
                }
                if (toMoment?.isValid() && started.isAfter(toMoment)) {
                    return false;
                }
            }

            if (columns.length && !columns.every((key) => (item[key] ?? 0) > 0)) {
                return false;
            }

            return true;
        });
    }

    private applyDisplayPage() {
        const filtered = this.getFilteredRows();
        const pageSize = Math.max(1, this.resultsprpage() || 1);
        const pages = Math.max(1, Math.ceil(filtered.length / pageSize) || 1);
        let pageNr = this.searchpagenr();
        if (pageNr >= pages) {
            pageNr = Math.max(0, pages - 1);
            this.searchpagenr(pageNr);
        }

        const start = pageNr * pageSize;
        this.data(filtered.slice(start, start + pageSize));
        this.searchpages(filtered.length ? pages : 0);
        this.searchtotalresults(filtered.length);

        if (!this.allData().length) {
            this.messageData({message: 'no data', type: "warning"});
        } else {
            this.messageData(null);
        }
    }

    private onPageChange() {
        this.applyDisplayPage();
    }

    private getParams(): ISearchParams {
        return {
            searchkey: "",
            resultsprpage: MAX_RESULTS_PER_PAGE,
            pagenr: 0,
            sortcol: "id",
            asc: false
        };
    }

    onRowClick(item: IImportHrFile) {
        if (this.routeDef?.child) {
            setRouteHash(this.routeDef.child, {importfileid: item.id});
        }
    }

    getImportRowDropdownParams(item: IImportHrFile): IEllipsisDropdownParams {
        return {
            btnClass: "btn btn-default btn-func",
            buttons: [
                {
                    icon: "fa-test-import",
                    label: "test import file",
                    validator: () => {
                        if (!global.permissionValidator.isMinOrganizationAdmin()) {
                            return false;
                        }
                        // Newest-first full list (not the filtered/paged view).
                        const index = this.allData().indexOf(item);
                        return index >= 0 && index < 10;
                    },
                    onClick: () => {
                        setRouteHash("testimportfile", {id: item.id});
                    },
                },
                {
                    icon: "fa-mail-bulk",
                    label: "send message|p",
                    onClick: () => {
                        global.dialogManager.addComponent({
                            title: `${i("fa-mail-bulk")} ((send message|p)) [${item.started}]`,
                            content: {
                                name: "component/messages/dialog/send-messages-by-message-flows",
                                params: {importHrFileId: item.id},
                            },
                            fullWidth: true,
                        });
                    },
                },
                {
                    icon: "fa-regular fa-file-chart-pie",
                    label: "Send report",
                    onClick: () => {
                        global.dialogManager.addComponent({
                            content: {
                                name: "component/import/dialog/send-end-report",
                                params: {importfile: item},
                            },
                            title: "Send report",
                        });
                    },
                },
                {
                    cls: "btn-separated",
                    icon: "fa-download",
                    label: "synccycle.btn.downloadhrfile",
                    validator: () =>
                        !!item.pathhrfile && this.isImportSyncCycleWithinDownloadAge(item),
                    onClick: () => {
                        const filepath = item.pathhrfile;
                        const filename = this.getImportFileBasename(filepath);
                        nsFile
                            .downloadFile({
                                filename,
                                params: {id: item.id},
                                servicename: "getimportfile",
                            })
                            .catch((reason: string) => this.messageData(createErrorMessage(String(reason))));
                    },
                },
                {
                    icon: "fa-download",
                    label: "synccycle.btn.downloadorgfile",
                    validator: () =>
                        !!item.pathhifile && this.isImportSyncCycleWithinDownloadAge(item),
                    onClick: () => {
                        const filepath = item.pathhifile;
                        const filename = this.getImportFileBasename(filepath);
                        nsFile
                            .downloadFile({
                                filename,
                                params: {id: item.id},
                                servicename: "getimportorgfile",
                            })
                            .catch((reason: string) => this.messageData(createErrorMessage(String(reason))));
                    },
                },
                {
                    icon: "fa-download",
                    label: "synccycle.btn.downloadclientlog",
                    validator: () =>
                        !!item.pathlogfile && this.isImportSyncCycleWithinDownloadAge(item),
                    onClick: () => {
                        const filepath = item.pathlogfile;
                        const filename = this.getImportFileBasename(filepath);
                        nsFile
                            .downloadFile({
                                filename,
                                params: {id: item.id},
                                servicename: "getimportlogfile",
                            })
                            .catch((reason: string) => this.messageData(createErrorMessage(String(reason))));
                    },
                },
            ],
        };
    }

    private getImportFileBasename(filepath: string): string {
        const index = Math.max(filepath.lastIndexOf("\\"), filepath.lastIndexOf("/"));
        return index >= 0 ? filepath.slice(index + 1) : filepath;
    }

    private static readonly importDownloadMaxAgeDays = 14;

    private isImportSyncCycleWithinDownloadAge(item: IImportHrFile): boolean {
        const started = convertFromServerString(item.started);
        if (!started.isValid()) {
            return false;
        }
        return moment().diff(started, "days") <= viewModel.importDownloadMaxAgeDays;
    }

    private getDisplayStarted(item: IImportHrFile) {
        return getImportHrFileStarted(item);
    }

    private getDateTitle(item: IImportHrFile) {
        const started = getImportHrFileStarted(item) ?? '';
        return `((start)) ${getDateTime(started, true)}\n((end)) ${getDateTime(item.ended ?? '', true)}`;
    }

    private getTypeTitle(file: IImportHrFile): string {
        return file.sequencenr ? `((delta synchronization)) #${file.sequencenr}` : 'full synchronization';
    }

    override dispose() {
        this.kosub.unsuscribeAll();
        this.reqSearch.abort();
        this.req_sendendreport.abort();
    }

}
